import { DecimalPipe } from '@angular/common';
import { HttpContext } from '@angular/common/http';
import {
  afterNextRender,
  Component,
  computed,
  effect,
  inject,
  model,
  output,
  untracked,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from '../../../../services/toast.service';
import { LOG_CONTENT } from '../../../../utils/http-context';
import { toObservableSignal } from 'ngxtension/to-observable-signal';
import { Align } from '../../../common/types';
import { Pagination } from '../../../pagination/pagination.component';
import { DataGridAdapter } from '../../data-grid.adapter';
import { DataGridStore } from '../../data-grid.store';
import { GridHeader } from '../../header/grid-header.component';
import { DataGridSearchOptions } from '../../types/pagination.type';
import { GridGallery } from '../../view/gallery/gallery.component';
import { GridTable } from '../../view/table/table.component';

@Component({
  host: {
    ngSkipHydration: 'true',
  },
  selector: 'app-ssp-data-grid',
  templateUrl: './ssp-data-grid.component.html',
  styleUrls: ['./ssp-data-grid.component.css'],
  standalone: true,
  imports: [
    GridTable,
    GridGallery,
    GridHeader,
    DecimalPipe,
    FormsModule,
    Pagination,
  ],
  providers: [DataGridStore],
})
export class ServerSidePaginationDataGrid extends DataGridAdapter {
  readonly router = inject(Router);
  readonly toastService = inject(ToastService);

  refresh = output<DataGridSearchOptions>();
  export = output<void>();

  page = model<number>(1);
  pageSize = model<number>(10);
  query = model<string>('');
  align = model<Align>('desc');
  orderBy = model<string>('createdAt');

  @ViewChild(GridHeader) header!: GridHeader;

  groupBy = this.store.groupBy;

  totalItems = computed(() => {
    if (this.pageInfo()?.totalItems) return this.pageInfo()?.totalItems;
    return this.rowData()?.length || 0;
  });

  searchOptions = toObservableSignal(
    computed(() => ({
      pageNo: this.page(),
      pageSize: this.pageSize(),
      query: this.query(),
      align: this.align(),
      orderBy: this.orderBy(),
    }))
  );

  constructor() {
    super();

    // query, align, orderBy, pageSize 변경 시 page를 1로 초기화
    effect(() => {
      this.query();
      this.align();
      this.orderBy();
      this.pageSize();

      untracked(() => {
        this.page.set(1);
      });
    });

    effect(() => {
      this.refresh.emit(this.searchOptions());
    });

    afterNextRender(() => {
      super.ngOnInit();

      // 데이터가 변경되면 체크박스로 선택된 데이터를 업데이트합니다.
      this.rowData$.subscribe(rowData => {
        if (this.selecteds() && this.selecteds().length > 0) {
          this.selecteds.set(
            this.selecteds().map(selected => {
              if (selected && selected['id']) {
                return rowData?.find(row => row['id'] === selected['id']);
              }

              return selected;
            })
          );
        }
      });

      // 검색 옵션이 변경되면 선택된 데이터를 초기화합니다.
      this.searchOptions.subscribe({
        next: () => {
          this.selecteds.set([]);
        },
      });
    });
  }

  /**
   * @description 엑셀 내보내기 이벤트를 처리합니다.
   * fetcher가 정의되어 있을 경우 자동으로 전체 데이터를 불러와 내보냅니다.
   */
  handleExport() {
    const fetcher = this.options()?.header?.fetcher;

    if (fetcher) {
      const params = {
        ...this.searchOptions(),
        pageSize: this.totalItems() || 1000,
        pageNo: 1,
      };

      this.toastService.info('전체 데이터를 불러오는 중입니다...');

      const context = new HttpContext().set(
        LOG_CONTENT,
        `[엑셀 다운로드] ${
          this.options()?.header?.excelFileName || '데이터_추출'
        }`
      );

      fetcher(params, context).subscribe({
        next: (res: { items: any[]; pageInfo: any }) => {
          this.header.exportData(res.items);
          this.toastService.success('엑셀 다운로드가 완료되었습니다.');
        },
        error: () => {
          this.toastService.error('데이터를 불러오는 중 오류가 발생했습니다.');
        },
      });
    } else {
      this.export.emit();
    }
  }
}
